import { and, eq, lt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { errorToString } from '@/lib/errors'
import { performMutations, useMutation } from '@/lib/mutations'
import { notify } from '@/lib/notice'
import { useStore } from '@/lib/pocketbase'
import type { Orgs } from '@/types/pbSchema'

export function useActiveEntriesBeforeDate(orgId: string | undefined, lockDate: string | null) {
    const [timeEntriesCollection] = useStore('time_entries')

    const { data: conflicts = [] } = useLiveQuery(
        q =>
            orgId && lockDate
                ? q
                      .from({ entry: timeEntriesCollection })
                      .where(({ entry }) =>
                          and(
                              eq(entry.org, orgId),
                              eq(entry.status, 'active'),
                              lt(entry.clock_in, lockDate)
                          )
                      )
                : undefined,
        [orgId, lockDate]
    )

    return conflicts.map(row => ({
        userId: row.user,
        entryId: row.id,
        clockIn: row.clock_in,
    }))
}

export function useLockPeriod(settings: Orgs | null) {
    const [orgsCollection, timeEntriesCollection] = useStore('orgs', 'time_entries')

    const lockMutation = useMutation({
        mutationFn: async ({ lockDate }: { lockDate: string }) => {
            if (!settings?.id) throw new Error('Organization not found')

            await performMutations(function* () {
                yield orgsCollection.update(settings.id, draft => {
                    Object.assign(draft, { entries_locked_until: lockDate })
                })
            })
        },
        onSuccess: () => {
            notify.success('Period locked')
        },
        onError: error => {
            notify.error(errorToString(error))
        },
    })

    const unlockMutation = useMutation({
        mutationFn: async () => {
            if (!settings?.id) throw new Error('Organization not found')

            await performMutations(function* () {
                yield orgsCollection.update(settings.id, draft => {
                    Object.assign(draft, { entries_locked_until: '' })
                })
            })
        },
        onSuccess: () => {
            notify.success('Period unlocked')
        },
        onError: error => {
            notify.error(errorToString(error))
        },
    })

    const bulkCloseAndLock = useMutation({
        mutationFn: async ({
            lockDate,
            activeEntryIds,
        }: {
            lockDate: string
            activeEntryIds: string[]
        }) => {
            if (!settings?.id) throw new Error('Organization not found')

            const now = new Date().toISOString()

            await performMutations(function* () {
                for (const entryId of activeEntryIds) {
                    yield timeEntriesCollection.update(entryId, draft => {
                        Object.assign(draft, {
                            status: 'pending',
                            clock_out: now,
                        })
                    })
                }
                yield orgsCollection.update(settings.id, draft => {
                    Object.assign(draft, { entries_locked_until: lockDate })
                })
            })
        },
        onSuccess: () => {
            notify.success('Active entries closed and period locked')
        },
        onError: error => {
            notify.error(errorToString(error))
        },
    })

    return {
        lockPeriod: lockMutation.mutate,
        unlockPeriod: unlockMutation.mutate,
        bulkCloseAndLock: bulkCloseAndLock.mutate,
        isLocking: lockMutation.isPending || bulkCloseAndLock.isPending,
        isUnlocking: unlockMutation.isPending,
    }
}
